import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useDebouncedValue } from "./use-debounced-value";

/** Tamaño de página estándar de todas las tablas server-side (convención P42). */
export const PAGE_SIZE = 15;

export interface ServerPagination<F> {
  filtros: F;
  setFiltros: Dispatch<SetStateAction<F>>;
  /** Mezcla parcial de filtros: `setFiltros(prev => ({ ...prev, ...parcial }))`. */
  patchFiltros: (parcial: Partial<F>) => void;
  /** Filtros con debounce · úsalos para armar los params del servidor. */
  filtrosDebounced: F;
  page: number;
  setPage: (p: number) => void;
  pageSize: number;
  /** Nº de filtros con valor (no vacío) · para el badge "N filtros activos". */
  filtrosActivos: number;
  /** Limpia filtros y vuelve a la página 1. */
  reset: () => void;
}

/**
 * Estado compartido de una tabla PAGINADA server-side (convención P42): página +
 * filtros con debounce + **reset automático a la página 1** cuando cambian los filtros.
 * Elimina el boilerplate que ~22 features repetían (F1): `useState(1)` de página,
 * `useDebouncedValue`, el `useEffect` de reset, el conteo de filtros activos y la
 * constante `PAGE_SIZE`. El mapeo filtros→params del API y el orden (sort) siguen
 * siendo específicos de cada feature.
 *
 * @example
 * const p = useServerPagination<FacturaFiltros>({});
 * const params = useMemo(
 *   () => ({ ...aParamsServer(p.filtrosDebounced, sort), page: p.page, pageSize: p.pageSize }),
 *   [p.filtrosDebounced, sort, p.page],
 * );
 * const { data } = useMisFacturas(params);
 */
export function useServerPagination<F extends object>(
  filtrosIniciales: F,
  opts?: { pageSize?: number; debounceMs?: number },
): ServerPagination<F> {
  const pageSize = opts?.pageSize ?? PAGE_SIZE;
  // Congelamos los iniciales en un ref · así `reset` no depende de que el caller
  // memoice el objeto literal (evita re-crear callbacks/efectos cada render).
  const inicialesRef = useRef(filtrosIniciales);
  const [filtros, setFiltros] = useState<F>(inicialesRef.current);
  const [page, setPage] = useState(1);
  const [filtrosDebounced] = useDebouncedValue(filtros, opts?.debounceMs ?? 350);

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtrosDebounced]);

  // Del filtro INMEDIATO (no el debounced) · el badge "N activos" da feedback al instante.
  const filtrosActivos = useMemo(
    () =>
      Object.values(filtros as Record<string, unknown>).filter(
        (v) => v != null && String(v).trim() !== "",
      ).length,
    [filtros],
  );

  const patchFiltros = useCallback(
    (parcial: Partial<F>) => setFiltros((prev) => ({ ...prev, ...parcial })),
    [],
  );

  const reset = useCallback(() => {
    setFiltros(inicialesRef.current);
    setPage(1);
  }, []);

  return {
    filtros,
    setFiltros,
    patchFiltros,
    filtrosDebounced,
    page,
    setPage,
    pageSize,
    filtrosActivos,
    reset,
  };
}
